import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import * as api from "../lib/api.ts";
import { notify, notifyError } from "../lib/notify.ts";

const SCORES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/**
 * How hard the whole session felt, 1 to 10, asked once it is over — the
 * coach's request, to read the load of a week beyond kilos and reps.
 * Queued like a set: the gym's signal is no reason to lose it.
 * `onScored` hears of a score the server has, not of one still queued.
 */
export function SessionEffort({
  sessionId,
  effort,
  onScored,
}: {
  sessionId: number;
  effort: number | null;
  onScored?: () => void;
}) {
  const client = useQueryClient();
  // Shown at once: a queued score only reaches the server's copy later.
  const [chosen, setChosen] = useState<number | null>(null);
  const shown = chosen ?? effort;

  const choose = async (score: number) => {
    setChosen(score);
    try {
      const result = await api.patchSession(sessionId, { perceivedEffort: score });
      navigator.vibrate?.(25);
      if (result.queued) notify("Hors ligne : note gardée, envoyée au retour du réseau.");
      else {
        onScored?.();
        await client.invalidateQueries({ queryKey: ["session", sessionId] });
      }
    } catch (error) {
      setChosen(null);
      notifyError("Note non enregistrée", error);
    }
  };

  return (
    <div className="mt-4 border-t border-line/60 pt-3">
      <p className="label">Difficulté de la séance</p>
      <div className="mt-2 grid grid-cols-5 gap-1.5" role="group" aria-label="Difficulté de la séance">
        {SCORES.map((score) => (
          <button
            key={score}
            type="button"
            aria-pressed={shown === score}
            onClick={() => void choose(score)}
            className={[
              "num h-11 rounded-card text-[1rem] font-bold",
              shown === score
                ? "bg-ink text-[var(--color-base)]"
                : "bg-surface-high text-ink-soft active:bg-surface-high/70",
            ].join(" ")}
          >
            {score}
          </button>
        ))}
      </div>
      <p className="mt-1.5 flex justify-between text-[0.78rem] text-ink-mute">
        <span>1 = facile</span>
        <span>10 = maximal</span>
      </p>
    </div>
  );
}
